import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type { User } from '@/types'

interface ProfileUpdate {
  first_name?: string
  last_name?: string
  phone?: string
  attributes?: Record<string, unknown>
}

interface TotpSetupResponse {
  secret: string
  provisioning_uri: string
  qr_code: string
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const updateUser = useAuthStore((s) => s.updateUser)

  return useMutation({
    mutationFn: async (data: ProfileUpdate) => {
      const { data: res } = await api.put<User>('/v1/users/me', data)
      return res
    },
    onSuccess: (user) => {
      updateUser(user)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useUploadAvatar() {
  const queryClient = useQueryClient()
  const updateUser = useAuthStore((s) => s.updateUser)

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data: res } = await api.post<User>('/v1/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res
    },
    onSuccess: (user) => {
      updateUser(user)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: { current_password: string; new_password: string }) => {
      const userId = useAuthStore.getState().user?.id
      await api.post(`/v1/users/${userId}/change-password`, data)
    },
  })
}

export function useTotpSetup() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<TotpSetupResponse>('/v1/users/me/totp-setup')
      return data
    },
  })
}

export function useTotpVerify() {
  const updateUser = useAuthStore((s) => s.updateUser)

  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<{ success: boolean }>('/v1/users/me/totp-verify', { code })
      return data
    },
    onSuccess: () => {
      updateUser({ totp_enabled: true } as Partial<User>)
    },
  })
}

export function useTotpDisable() {
  const updateUser = useAuthStore((s) => s.updateUser)

  return useMutation({
    mutationFn: async (password: string) => {
      const { data } = await api.post<{ success: boolean }>('/v1/users/me/totp-disable', { password })
      return data
    },
    onSuccess: () => {
      updateUser({ totp_enabled: false } as Partial<User>)
    },
  })
}
